import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { collection, getDocs, getFirestore, limit, query } from 'firebase/firestore';
import { useTheme } from '../../contexts/ThemeContext';
import { usePopup } from '../../contexts/PopupContext';
import {
  createTourInvite,
  resendTourInvite,
  revokeTourInvite,
  subscribeTourInvitesForTour,
} from '../../utils/tourInviteService';
import {
  normalizeEmail,
  memberDisplayName,
  resolveUserTypeMeta,
  buildInviteMemberRecord,
  fetchProfilesForParty,
} from '../../utils/tourPartyMemberUtils';
import { TourPartyMemberCard } from './TourPartyMemberCard';
import { TourPartyPermissionsPanel } from './TourPartyPermissionsPanel';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import { canManageTourParty } from '../../utils/tourMemberPermissionsService';
import {
  formatCentsAsDollars,
  TOUR_PARTY_CREW_SEAT_PRICE_CENTS,
} from '../../constants/subscriptionPlans';
import {
  subscribeTourPartyBilling,
  startTourPartyCrewCheckout,
} from '../../utils/tourPartySubscriptionService';

export function TourPartyPanel({
  ownerId,
  ownerName,
  ownerEmail,
  tourId,
  tourName,
  currentUserId,
  tourShareSettings = null,
  theme,
  onError,
  onSuccess,
}) {
  const db = getFirestore();
  const { isDarkMode } = useTheme();
  const { showConfirmation } = usePopup();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profileById, setProfileById] = useState(new Map());
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inviteQueue, setInviteQueue] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [message, setMessage] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [partyBilling, setPartyBilling] = useState(null);
  const [crewCheckoutBusy, setCrewCheckoutBusy] = useState(false);

  useEffect(() => {
    if (!ownerId || !tourId) {
      setInvites([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = subscribeTourInvitesForTour(db, ownerId, tourId, (rows) => {
      setInvites(rows);
      setLoading(false);
    });
    return unsub;
  }, [db, ownerId, tourId]);

  const activeInvites = useMemo(
    () => invites.filter((r) => !['revoked', 'cancelled'].includes(String(r.status || '').toLowerCase())),
    [invites]
  );

  useEffect(() => {
    if (!ownerId) {
      setProfileById(new Map());
      return undefined;
    }
    let cancelled = false;
    setProfilesLoading(true);
    fetchProfilesForParty(db, { ownerId, invites: activeInvites })
      .then((map) => {
        if (!cancelled) setProfileById(map);
      })
      .catch((err) => console.warn('TourPartyPanel profiles', err))
      .finally(() => {
        if (!cancelled) setProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [db, ownerId, activeInvites]);

  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);
  const glassStyles = useCalendarGlassStyles(theme, isDarkMode);
  const canManageParty = canManageTourParty(currentUserId, ownerId, tourShareSettings);
  const isTourOwner = Boolean(currentUserId && ownerId && currentUserId === ownerId);

  useEffect(() => {
    if (!isTourOwner || !ownerId || !tourId) {
      setPartyBilling(null);
      return undefined;
    }
    return subscribeTourPartyBilling(ownerId, tourId, setPartyBilling, (err) => {
      console.warn('TourPartyPanel billing', err);
    });
  }, [isTourOwner, ownerId, tourId]);

  const enrichedInvites = useMemo(
    () => activeInvites.map((row) => buildInviteMemberRecord(row, profileById)),
    [activeInvites, profileById]
  );

  const acceptedCrewCount = useMemo(
    () => enrichedInvites.filter((m) => {
      if (String(m.status || '').toLowerCase() !== 'accepted') return false;
      const uid = String(m.id || '').trim();
      return uid && uid !== ownerId && m.onPlatform !== false;
    }).length,
    [enrichedInvites, ownerId]
  );

  const crewBillingStats = useMemo(() => {
    const assignedUids = partyBilling?.assignedUids || {};
    const assignedCount = Object.keys(assignedUids).filter((id) => assignedUids[id]).length;
    const seatsPurchased = Math.max(0, Number(partyBilling?.seatsPurchased) || 0);
    const periodEnd = partyBilling?.periodEnd ? new Date(partyBilling.periodEnd) : null;
    const periodActive = periodEnd && periodEnd.getTime() > Date.now();
    const coveredCount = periodActive ? assignedCount : 0;
    const seatsToBuy = Math.max(0, acceptedCrewCount - coveredCount);
    return {
      assignedCount,
      seatsPurchased,
      periodEnd,
      periodActive,
      seatsToBuy,
    };
  }, [partyBilling, acceptedCrewCount]);

  const handlePayForCrewSeats = async () => {
    const seatCount = crewBillingStats.seatsToBuy;
    if (!tourId || seatCount < 1) {
      onError?.('No crew seats to purchase right now.');
      return;
    }
    setCrewCheckoutBusy(true);
    try {
      const origin = Platform.OS === 'web' && typeof window !== 'undefined'
        ? window.location.origin
        : 'https://venuereportcard.com';
      const data = await startTourPartyCrewCheckout({
        tourId,
        seatCount,
        successUrl: `${origin}/calendar?crewSeats=added`,
        cancelUrl: `${origin}/calendar?crewSeats=cancelled`,
      });
      const url = data?.url;
      if (!url) throw new Error('Checkout URL was not returned.');
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    } catch (err) {
      onError?.(err?.message || 'Could not start crew checkout.');
    } finally {
      setCrewCheckoutBusy(false);
    }
  };

  const previewMemberFromUser = useCallback(
    (user) => {
      const typeMeta = resolveUserTypeMeta(user?.userType);
      return {
        id: user.id,
        name: memberDisplayName(user),
        email: user.email,
        username: user.username,
        photoURL: user.photoURL,
        userTypeLabel: typeMeta.label,
        userTypeColor: typeMeta.color,
      };
    },
    []
  );

  const invitedEmails = useMemo(() => {
    const set = new Set();
    activeInvites.forEach((r) => {
      const email = normalizeEmail(r.inviteeEmail);
      if (email) set.add(email);
    });
    if (ownerEmail) set.add(normalizeEmail(ownerEmail));
    return set;
  }, [activeInvites, ownerEmail]);

  const queueEmails = useMemo(
    () => new Set(inviteQueue.map((q) => normalizeEmail(q.email)).filter(Boolean)),
    [inviteQueue]
  );

  const isEmailUnavailable = useCallback(
    (email) => {
      const lower = normalizeEmail(email);
      return !lower || invitedEmails.has(lower) || queueEmails.has(lower);
    },
    [invitedEmails, queueEmails]
  );

  const runUserSearch = useCallback(async (term) => {
    setSearch(term);
    if (term.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), limit(150)));
      const lower = term.toLowerCase();
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.id !== ownerId)
        .filter((u) => normalizeEmail(u.email))
        .filter((u) => !invitedEmails.has(normalizeEmail(u.email)))
        .filter((u) => {
          const hay = [
            u.firstName,
            u.lastName,
            u.username,
            u.email,
            `${u.firstName || ''} ${u.lastName || ''}`,
          ].join(' ').toLowerCase();
          return hay.includes(lower);
        })
        .slice(0, 6);
      setSearchResults(rows);
    } catch (err) {
      console.warn('Tour party user search', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [ownerId, invitedEmails]);

  const visibleSearchResults = useMemo(
    () => searchResults.filter((u) => !queueEmails.has(normalizeEmail(u.email))),
    [searchResults, queueEmails]
  );

  const addUserToQueue = (user) => {
    const email = normalizeEmail(user.email);
    if (!email || isEmailUnavailable(email)) return;
    setInviteQueue((prev) => [
      ...prev,
      {
        key: user.id || email,
        userId: user.id,
        email,
        name: memberDisplayName(user),
        phone: String(user.phone || user.phoneNumber || '').trim(),
        photoURL: user.photoURL || '',
        username: user.username || '',
        userType: user.userType || 'member',
        interests: Array.isArray(user.interests) ? user.interests : [],
        source: 'user',
      },
    ]);
    setSearch('');
    setSearchResults([]);
  };

  const addManualToQueue = () => {
    const email = normalizeEmail(manualEmail);
    if (!email) {
      onError?.('Email is required.');
      return;
    }
    if (isEmailUnavailable(email)) {
      onError?.('That email is already on your invite list.');
      return;
    }
    setInviteQueue((prev) => [
      ...prev,
      {
        key: `manual-${email}`,
        email,
        name: manualName.trim(),
        phone: manualPhone.trim(),
        photoURL: '',
        username: '',
        userType: '',
        source: 'manual',
      },
    ]);
    setManualName('');
    setManualEmail('');
    setManualPhone('');
    setShowManualForm(false);
  };

  const removeFromQueue = (key) => {
    setInviteQueue((prev) => prev.filter((q) => q.key !== key));
  };

  const handleSendInvites = async () => {
    if (inviteQueue.length === 0) {
      onError?.('Add at least one person to invite.');
      return;
    }
    try {
      setSending(true);
      const sharedMessage = message.trim();
      let sent = 0;
      for (const person of inviteQueue) {
        await createTourInvite({
          ownerId,
          ownerName,
          ownerEmail,
          tourId,
          tourName,
          inviteeEmail: person.email,
          inviteeName: person.name,
          inviteePhone: person.phone,
          message: sharedMessage,
          senderId: currentUserId,
        });
        sent += 1;
      }
      setInviteQueue([]);
      setMessage('');
      onSuccess?.(
        sent === 1 ? 'Invitation sent.' : `${sent} invitations sent.`
      );
    } catch (err) {
      onError?.(err?.message || 'Could not send invite.');
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (inviteId, wasPending) => {
    try {
      await revokeTourInvite(inviteId, ownerId);
      onSuccess?.(wasPending ? 'Invitation revoked.' : 'Removed from tour party.');
    } catch (err) {
      onError?.(err?.message || 'Could not remove from tour party.');
    }
  };

  const handleResend = async (inviteId) => {
    try {
      setResendingId(inviteId);
      await resendTourInvite(inviteId, ownerId, currentUserId);
      onSuccess?.('Invitation email resent.');
    } catch (err) {
      onError?.(err?.message || 'Could not resend invite.');
    } finally {
      setResendingId(null);
    }
  };

  const memberLabel = (member) => member.name || member.email || 'this person';

  const confirmResend = (member) => {
    showConfirmation(
      'Resend invitation?',
      `Send another invitation email to ${memberLabel(member)}?`,
      () => handleResend(member.inviteId),
      null,
      { confirmLabel: 'Resend email', confirmVariant: 'primary', iconName: 'mail-outline' }
    );
  };

  const confirmRemove = (member) => {
    const isPending = String(member.status || 'pending').toLowerCase() === 'pending';
    showConfirmation(
      isPending ? 'Revoke invitation?' : 'Remove from tour party?',
      isPending
        ? `Revoke the pending invitation for ${memberLabel(member)}? They will no longer be able to accept it.`
        : `Remove ${memberLabel(member)} from this tour party?`,
      () => handleRemove(member.inviteId, isPending),
      null,
      {
        confirmLabel: isPending ? 'Revoke' : 'Remove',
        confirmVariant: 'danger',
        iconName: isPending ? 'close-circle-outline' : 'person-remove-outline',
      }
    );
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.lead}>
        {canManageParty
          ? 'Search members and add them to your invite list. Everyone gets an email and an in-app message to accept or decline.'
          : 'View tour party members for this tour. Contact the tour owner to change invitations.'}
      </Text>

      {isTourOwner ? (
        <View style={[glassStyles.card, { marginBottom: 12 }]}>
          <Text style={styles.cardTitle}>Crew Map + Calendar access</Text>
          <Text style={styles.cardHint}>
            Tour party members do not pay for their own subscription. You cover their VRC Member
            access upfront ({formatCentsAsDollars(TOUR_PARTY_CREW_SEAT_PRICE_CENTS)} per accepted
            member per 30 days).
          </Text>
          <Text style={[styles.cardHint, { marginTop: 8 }]}>
            Accepted crew on platform: {acceptedCrewCount}
            {crewBillingStats.periodActive
              ? ` · Covered through ${crewBillingStats.periodEnd?.toLocaleDateString()} (${crewBillingStats.assignedCount}/${crewBillingStats.seatsPurchased} seats assigned)`
              : ' · No active crew billing period'}
          </Text>
          {crewBillingStats.seatsToBuy > 0 ? (
            <TouchableOpacity
              style={[styles.primaryBtn, crewCheckoutBusy && { opacity: 0.6 }]}
              onPress={handlePayForCrewSeats}
              disabled={crewCheckoutBusy}
            >
              {crewCheckoutBusy ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.primaryBtnText}>
                  Pay for {crewBillingStats.seatsToBuy} crew seat
                  {crewBillingStats.seatsToBuy === 1 ? '' : 's'} (
                  {formatCentsAsDollars(crewBillingStats.seatsToBuy * TOUR_PARTY_CREW_SEAT_PRICE_CENTS)}
                  )
                </Text>
              )}
            </TouchableOpacity>
          ) : (
            <Text style={[styles.cardHint, { marginTop: 8, color: '#2ecc71' }]}>
              All accepted crew are covered for this billing period.
            </Text>
          )}
        </View>
      ) : null}

      <TourPartyPermissionsPanel
        ownerId={ownerId}
        currentUserId={currentUserId}
        tourId={tourId}
        tourShareSettings={tourShareSettings}
        acceptedMembers={enrichedInvites}
        theme={theme}
        onError={onError}
        onSuccess={onSuccess}
      />

      {canManageParty ? (
        <>
      {/* Queue */}
      <View style={glassStyles.card}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Ready to invite</Text>
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeText}>{inviteQueue.length}</Text>
          </View>
        </View>
        {inviteQueue.length === 0 ? (
          <Text style={styles.cardHint}>
            Search below and tap someone to add them here. You can add multiple people before sending.
          </Text>
        ) : (
          <View style={styles.queueList}>
            {inviteQueue.map((person) => {
              const preview = previewMemberFromUser({
                id: person.userId || person.key,
                firstName: person.name,
                email: person.email,
                username: person.username,
                photoURL: person.photoURL,
                userType: person.userType,
                interests: person.interests,
              });
              return (
                <TourPartyMemberCard
                  key={person.key}
                  member={{
                    ...preview,
                    name: person.name || person.email,
                    role: person.source === 'manual' ? 'Not on VRC yet' : undefined,
                  }}
                  theme={theme}
                  isDarkMode={isDarkMode}
                  compact
                  trailing={
                    <TouchableOpacity
                      onPress={() => removeFromQueue(person.key)}
                      hitSlop={8}
                      accessibilityLabel={`Remove ${person.name || person.email}`}
                    >
                      <Ionicons name="close-circle" size={20} color={theme.text.tertiary} />
                    </TouchableOpacity>
                  }
                />
              );
            })}
          </View>
        )}
      </View>

      {/* Search */}
      <View style={glassStyles.card}>
        <Text style={styles.cardTitle}>Search members</Text>
        <View style={styles.searchField}>
          <Ionicons name="search-outline" size={16} color={theme.text.tertiary} />
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={runUserSearch}
            placeholder="Name, username, or email…"
            placeholderTextColor={theme.input.placeholder}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {searchLoading ? <ActivityIndicator size="small" color={theme.brand.accent} /> : null}
        </View>

        {search.trim().length >= 2 && !searchLoading && visibleSearchResults.length === 0 ? (
          <Text style={styles.cardHint}>No matching members found.</Text>
        ) : null}

        {visibleSearchResults.length > 0 ? (
          <View style={styles.searchResults}>
            {visibleSearchResults.map((u) => (
              <TourPartyMemberCard
                key={u.id}
                member={previewMemberFromUser(u)}
                theme={theme}
                isDarkMode={isDarkMode}
                onPress={() => addUserToQueue(u)}
                trailing={
                  <View style={styles.addBtn}>
                    <Ionicons name="add" size={18} color={theme.button.primaryText} />
                  </View>
                }
              />
            ))}
          </View>
        ) : null}

        <TouchableOpacity
          style={styles.manualToggle}
          onPress={() => setShowManualForm((v) => !v)}
        >
          <Ionicons
            name={showManualForm ? 'chevron-up' : 'mail-outline'}
            size={14}
            color={theme.brand.accent}
          />
          <Text style={styles.manualToggleText}>
            {showManualForm ? 'Hide email invite' : 'Invite someone not on VRC yet'}
          </Text>
        </TouchableOpacity>

        {showManualForm ? (
          <View style={styles.manualForm}>
            <TextInput
              style={styles.input}
              value={manualName}
              onChangeText={setManualName}
              placeholder="Name"
              placeholderTextColor={theme.input.placeholder}
            />
            <TextInput
              style={styles.input}
              value={manualEmail}
              onChangeText={setManualEmail}
              placeholder="Email (required)"
              placeholderTextColor={theme.input.placeholder}
              autoCapitalize="none"
              keyboardType="email-address"
            />
            <TextInput
              style={styles.input}
              value={manualPhone}
              onChangeText={setManualPhone}
              placeholder="Phone (optional)"
              placeholderTextColor={theme.input.placeholder}
              keyboardType="phone-pad"
            />
            <TouchableOpacity style={styles.secondaryBtn} onPress={addManualToQueue}>
              <Ionicons name="person-add-outline" size={15} color={theme.brand.accent} />
              <Text style={styles.secondaryBtnText}>Add to invite list</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      {/* Message + send */}
      {inviteQueue.length > 0 ? (
        <View style={glassStyles.card}>
          <Text style={styles.cardTitle}>Message for everyone</Text>
          <Text style={styles.cardHint}>Optional — included in each invite.</Text>
          <TextInput
            style={[styles.input, styles.messageInput]}
            value={message}
            onChangeText={setMessage}
            placeholder="Hey! Join our tour on Venue Report Card…"
            placeholderTextColor={theme.input.placeholder}
            multiline
            textAlignVertical="top"
          />
          <TouchableOpacity
            style={[styles.primaryBtn, sending && styles.primaryBtnDisabled]}
            onPress={handleSendInvites}
            disabled={sending}
          >
            {sending ? (
              <ActivityIndicator color={theme.button.primaryText} size="small" />
            ) : (
              <>
                <Ionicons name="paper-plane-outline" size={16} color={theme.button.primaryText} />
                <Text style={styles.primaryBtnText}>
                  Send {inviteQueue.length} invitation{inviteQueue.length === 1 ? '' : 's'}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      ) : null}
        </>
      ) : null}

      {/* Existing party */}
      <View style={glassStyles.card}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Tour party</Text>
          <Text style={styles.cardMeta}>{activeInvites.length} invited</Text>
        </View>
        {loading || profilesLoading ? (
          <ActivityIndicator color={theme.brand.accent} style={{ marginTop: 8 }} />
        ) : enrichedInvites.length === 0 ? (
          <Text style={styles.cardHint}>No one invited yet.</Text>
        ) : (
          enrichedInvites.map((member) => (
            <TourPartyMemberCard
              key={member.inviteId}
              member={member}
              theme={theme}
              isDarkMode={isDarkMode}
              compact
              trailing={
                canManageParty ? (
                <View style={styles.trailingActions}>
                  {String(member.status || 'pending').toLowerCase() === 'pending' ? (
                    <TouchableOpacity
                      onPress={() => confirmResend(member)}
                      disabled={resendingId === member.inviteId}
                      hitSlop={8}
                      accessibilityLabel="Resend invitation email"
                    >
                      {resendingId === member.inviteId ? (
                        <ActivityIndicator size="small" color={theme.brand.accent} />
                      ) : (
                        <Ionicons name="mail-outline" size={16} color={theme.brand.accent} />
                      )}
                    </TouchableOpacity>
                  ) : null}
                  <TouchableOpacity
                    onPress={() => confirmRemove(member)}
                    hitSlop={8}
                    accessibilityLabel="Remove from tour party"
                  >
                    <Ionicons name="trash-outline" size={16} color={theme.status.error} />
                  </TouchableOpacity>
                </View>
                ) : null
              }
            />
          ))
        )}
      </View>
    </View>
  );
}

function createStyles(theme, isDarkMode) {
  const insetBg = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  return StyleSheet.create({
    wrap: { gap: 10 },
    trailingActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingRight: 10,
    },
    lead: {
      fontSize: 12,
      lineHeight: 17,
      color: theme.text.secondary,
      marginBottom: 2,
    },
    cardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
    },
    cardTitle: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.text.primary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    cardMeta: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.text.tertiary,
    },
    cardHint: {
      fontSize: 12,
      lineHeight: 16,
      color: theme.text.secondary,
    },
    countBadge: {
      minWidth: 22,
      height: 22,
      borderRadius: 11,
      paddingHorizontal: 6,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.brand.primary,
    },
    countBadgeText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.button.primaryText,
    },
    searchField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      paddingHorizontal: 10,
      paddingVertical: 8,
      backgroundColor: insetBg,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: theme.input.text,
      padding: 0,
    },
    searchResults: {
      gap: 6,
    },
    searchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    queueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    searchInfo: {
      flex: 1,
      minWidth: 0,
    },
    searchRowTitle: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.text.primary,
    },
    searchRowUsername: {
      fontSize: 12,
      color: theme.brand.accent,
      marginTop: 1,
    },
    searchRowSub: {
      fontSize: 11,
      color: theme.text.secondary,
      marginTop: 2,
    },
    addBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.button.primary,
    },
    avatar: {
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.elevated,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
    },
    avatarInitial: {
      fontWeight: '800',
    },
    typePill: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 999,
      borderWidth: 1,
    },
    typePillText: {
      fontSize: 10,
      fontWeight: '700',
    },
    queueList: { gap: 6 },
    queueInfo: { flex: 1, minWidth: 0, gap: 2 },
    queueName: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.text.primary,
    },
    queueEmail: {
      fontSize: 11,
      color: theme.text.secondary,
    },
    queueManualTag: {
      fontSize: 10,
      fontWeight: '600',
      color: theme.text.tertiary,
      marginTop: 2,
    },
    manualToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingTop: 4,
    },
    manualToggleText: {
      fontSize: 12,
      fontWeight: '700',
      color: theme.brand.accent,
    },
    manualForm: { gap: 8, marginTop: 4 },
    input: {
      borderWidth: 1,
      borderColor: theme.ui.border,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 14,
      color: theme.input.text,
      backgroundColor: insetBg,
    },
    messageInput: {
      minHeight: 72,
    },
    secondaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.brand.accent,
    },
    secondaryBtnText: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.brand.accent,
    },
    primaryBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      backgroundColor: theme.button.primary,
      borderRadius: 10,
      paddingVertical: 12,
      marginTop: 4,
    },
    primaryBtnDisabled: { opacity: 0.55 },
    primaryBtnText: {
      color: theme.button.primaryText,
      fontWeight: '800',
      fontSize: 14,
    },
    memberRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 8,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: theme.background.secondary,
      marginTop: 4,
    },
    memberAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.elevated,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    memberAvatarText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.text.secondary,
    },
    memberName: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.text.primary,
    },
    memberEmail: {
      fontSize: 11,
      color: theme.text.secondary,
      marginTop: 2,
    },
    statusPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 999,
      borderWidth: 1,
    },
    statusText: {
      fontSize: 10,
      fontWeight: '700',
    },
  });
}

export default TourPartyPanel;
